import { Injectable } from '@angular/core'
import { formatNumber } from '@angular/common'
import { format, formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'
import { TransifexService } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { type FuelPriceDTO } from './vehicles.service'
import { type FormattedPrice, type GasPricesPlacePrediction, type ResolvedLocation } from '../models/fuel-prices.models'

@Injectable({
  providedIn: 'root'
})
export class FuelPricesService {
  private readonly currency: string
  private readonly currencyLocale: string

  constructor (
    private readonly transifexService: TransifexService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.currency = this.remoteConfigService.get('currency')
    this.currencyLocale = this.remoteConfigService.get('currencyLocale')
  }

  mapAddressComponents (place): ResolvedLocation {
    const zipComponent = place.address_components?.find(component =>
      component.types?.includes('postal_code')
    )

    const location = place.geometry?.location
    let lat
    let lon
    if (location?.lat != null) {
      lat = location.lat()
    }
    if (location?.lng != null) {
      lon = location.lng()
    }

    return {
      zip: zipComponent?.long_name,
      lat,
      lon
    }
  }

  displayPrediction (prediction: GasPricesPlacePrediction | null): string {
    return prediction?.description ?? ''
  }

  styleBlock (fuelStation: FuelPriceDTO): string {
    return fuelStation.openNow === true
      ? 'w-[14px] h-[14px] min-w-[14px] min-h-[14px] bg-mat-ef-green-secondary-500'
      : 'w-[14px] h-[14px] min-w-[14px] min-h-[14px] bg-mat-ef-red-secondary-500'
  }

  getOpenNowTranslation (fuelStation: FuelPriceDTO): string {
    return fuelStation.openNow === true
      ? this.transifexService.getTransifexTranslation('Now open', 'customerPortal.customer-portal.vehicle.nowOpen')
      : this.transifexService.getTransifexTranslation('Now closed', 'customerPortal.customer-portal.vehicle.nowClosed')
  }

  // German fuel prices show three decimals with the last digit raised
  // (the 9/10 cent), e.g. 1,04^9 €. Truncate (not round) to keep the shown
  // digits identical to the source, matching the clever-tanken convention.
  formatPrice (priceStr: string): FormattedPrice {
    const value = Number(priceStr)
    if (priceStr == null || Number.isNaN(value)) {
      return { main: priceStr ?? '', last: '', symbol: '' }
    }
    const [intPart, dec] = value.toFixed(3).split('.')
    return {
      main: formatNumber(Number(`${intPart}.${dec.slice(0, 2)}`), this.currencyLocale, '1.2-2'),
      last: dec.slice(2, 3),
      symbol: this.getCurrencySymbol()
    }
  }

  // getCurrencySymbol is deprecated; Intl.NumberFormat().formatToParts() is
  // Angular's recommended replacement for extracting the narrow currency symbol.
  private getCurrencySymbol (): string {
    return new Intl.NumberFormat(this.currencyLocale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol'
    })
      .formatToParts()
      .find(part => part.type === 'currency')?.value ?? this.currency
  }

  getUseMyLocationTranslation (): string {
    return this.transifexService.getTransifexTranslation(
      'Use my location',
      'customerPortal.customer-portal.gas-prices.use-my-location'
    )
  }

  getClearLocationTranslation (): string {
    return this.transifexService.getTransifexTranslation(
      'Clear location',
      'customerPortal.customer-portal.gas-prices.clear-location'
    )
  }

  getReportedAgo (fuelStation: FuelPriceDTO): string {
    return fuelStation.reportedAt == null
      ? ''
      : formatDistanceToNow(new Date(fuelStation.reportedAt), { addSuffix: true, locale: de })
  }

  getOpensNext (fuelStation: FuelPriceDTO): string {
    return fuelStation.openNext == null
      ? ''
      : format(new Date(fuelStation.openNext), 'EEEEEE HH:mm', { locale: de })
  }

  // The backend reports the distance to the station in meters; the table
  // shows kilometers with a single decimal, e.g. 1298 -> 1,3 km.
  getDistance (fuelStation: FuelPriceDTO): string {
    const distance = Number(fuelStation.distance)
    if (fuelStation.distance == null || Number.isNaN(distance)) {
      return ''
    }
    return formatNumber(distance / 1000, this.currencyLocale, '1.1-1') + ' km'
  }

  getFuelTypeLabel (fuelType: string): string {
    const key = 'customerPortal.customer-portal.gas-prices.fuel-type.' + fuelType.toLowerCase()
    return this.transifexService.getTransifexTranslation(fuelType, key)
  }
}
